import {
  useLazyPurchaseOrderCreatePreCheckQuery,
  usePurchaseOrderItemBrandsLockMutation,
  usePurchaseOrderItemBrandsQuery,
  usePurchaseOrderItemBrandsUnlockMutation,
  usePurchaseOrderItemBrandUnlockMutation,
} from 'api';
import { Form } from 'components';
import { useWatch } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { dateFormatter } from 'constants/index';
import React, { useCallback, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { PreCheckMessageType } from 'pages/admin/purchaseOrders/add/preCreateCheckModal';

export const useAddEdit = () => {
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [purchaseOrderItemIds, setPurchaseOrderItemIds] = useState<React.Key[]>([]);
  const [supplierOfferModalState, setSupplierOfferModalState] = useState<boolean>(false);
  const [chooseSupplierModalState, setChooseSupplierModalState] = useState<boolean>(false);
  const [preCreateCheckModalState, setPreCreateCheckModalState] = useState<boolean>(false);
  const [purchaseOrderItems, setPurchaseOrderItems] = useState<Record<string, any>[]>([]);
  const [preCheckMessage, setPreCheckMessage] = useState<PreCheckMessageType | undefined>(undefined);
  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const [form] = Form.useForm();
  const brandIds: string[] = useWatch('brandIds', form);
  const navigate = useNavigate();
  const { handleSuccess, handleError } = useNotificationMessages();

  const { data: brandsData = [] } = usePurchaseOrderItemBrandsQuery();
  const [createPreCheck] = useLazyPurchaseOrderCreatePreCheckQuery();
  const [lockBrands] = usePurchaseOrderItemBrandsLockMutation();
  const [unlockBrands] = usePurchaseOrderItemBrandsUnlockMutation();
  const [unlockBrand] = usePurchaseOrderItemBrandUnlockMutation();

  const onValuesChange = useCallback(
    (value: { [key: string]: any }) => {
      const _filters: any = {
        ...value,
        orderConfirmationDateFrom: value.orderConfirmationDate?.[0]
          ? dateFormatter(value.orderConfirmationDate[0], 'start')
          : undefined,
        orderConfirmationDateTo: value.orderConfirmationDate?.[1]
          ? dateFormatter(value.orderConfirmationDate[1], 'end')
          : undefined,
        deliveryDateFrom: value.deliveryDate?.[0] ? dateFormatter(value.deliveryDate[0], 'start') : undefined,
        deliveryDateTo: value.deliveryDate?.[1] ? dateFormatter(value.deliveryDate[1], 'end') : undefined,
      };
      _filters.orderConfirmationDate = undefined;
      _filters.deliveryDate = undefined;
      if (_filters.itemStatus === 'ALL') {
        _filters.itemStatus = undefined;
      }
      if ('brandIds' in value) {
        form.setFieldsValue({ itemId: undefined });
        _filters.itemId = undefined;
      }
      setFilters((prevState) => ({ ...prevState, ..._filters }));
    },
    [form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    navigate(ADMIN_ROUTES.purchaseOrder.list);
  }, [form, navigate]);

  const onRowSelect = useCallback((ids: React.Key[], values: Record<string, any>[]) => {
    setPurchaseOrderItemIds(ids);
    setPurchaseOrderItems(values);
  }, []);
  const onReset = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      form.resetFields();
      setFilters({});
    },
    [form]
  );

  const onChooseSupplierModalStateChange = useCallback(
    () => setChooseSupplierModalState((prevState) => !prevState),
    []
  );
  const onPreCreateCheckModalStateChange = useCallback(
    () => setPreCreateCheckModalState((prevState) => !prevState),
    []
  );
  const onSupplierOfferModalStateChange = useCallback(() => setSupplierOfferModalState((prevState) => !prevState), []);
  const onCreateOrder = useCallback(async () => {
    const { data } = await createPreCheck(purchaseOrderItemIds);
    if (data === 'OK') {
      setChooseSupplierModalState((prevState) => !prevState);
    } else {
      setPreCheckMessage(data as PreCheckMessageType);
      setPreCreateCheckModalState((prevState) => !prevState);
    }
  }, [createPreCheck, purchaseOrderItemIds]);
  const onAssign = useCallback(async () => {
    try {
      const { data, error } = await lockBrands(brandIds);
      if (data?.success) {
        handleSuccess('Brand(s) successfully assigned to you.');
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [brandIds, handleError, handleSuccess, lockBrands]);
  const onResetLockedBrands = useCallback(async () => {
    try {
      const { data, error } = await unlockBrands();
      if (data?.success) {
        handleSuccess('Brand(s) successfully unassigned from you.');
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [handleError, handleSuccess, unlockBrands]);
  const onBrandUnlock = useCallback(
    async (brandId: string) => {
      try {
        const { data, error } = await unlockBrand(brandId);
        if (data?.success) {
          handleSuccess('Brand successfully unassigned.');
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [handleError, handleSuccess, unlockBrand]
  );
  const onPriceRequest = useCallback(
    () => navigate(`${ADMIN_ROUTES.purchaseOrder.requestPrice}?items=${purchaseOrderItemIds.join(',')}`),
    [navigate, purchaseOrderItemIds]
  );

  const isAllSelectedBrandsLocked = useMemo(() => {
    return brandIds?.every((id: string) =>
      brandsData.find((item: Record<string, any>) => item.id === id && item.locked)
    );
  }, [brandIds, brandsData]);

  const hasLockedBrandByUser = useMemo(() => {
    return brandsData?.some((brand: Record<string, any>) => brand.locked && brand.lockedBy.id === userId);
  }, [brandsData, userId]);
  return {
    form,
    onCancel,
    onValuesChange,
    brandIds,
    filters,
    onRowSelect,
    onChooseSupplierModalStateChange,
    chooseSupplierModalState,
    onCreateOrder,
    purchaseOrderItems,
    onReset,
    onPriceRequest,
    supplierOfferModalState,
    onSupplierOfferModalStateChange,
    onAssign,
    onResetLockedBrands,
    brandsData,
    isAllSelectedBrandsLocked,
    purchaseOrderItemIds,
    onBrandUnlock,
    hasLockedBrandByUser,
    preCreateCheckModalState,
    onPreCreateCheckModalStateChange,
    preCheckMessage,
  };
};
